import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  MenuItem,
  useTheme
} from '@mui/material';
import Category from '@mui/icons-material/Category';
import CloseIcon from '@mui/icons-material/Close';
import { FuelType } from '../types';

interface FuelTypeFormModalProps {
  openFT: boolean;
  onClose: () => void;
  onSubmit: (data: FuelType) => void;   // <-- pass data back
  fuelTypeData?: FuelType;
  isEditMode?: boolean;
}

const commonTextFieldStyles = {
  mb: 2,
  backgroundColor: 'white',
  borderRadius: 1
};

const units = [
  "Gallons",
  "Litres (L)",
  "Cubic feet (cf)",
  "Cubic meters (m3)",
  "Gigajoules (GJ)",
  "Kilograms (kg)",
  "Kilowatt-hours (kWh)"
];

const AddNewFuelTypePopUp: React.FC<FuelTypeFormModalProps> = ({
  openFT,
  onClose,
  onSubmit,
  fuelTypeData,
  isEditMode
}) => {
  // Local state for form fields
  const theme = useTheme();
  const [fuelName, setFuelName] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [costPerUnit, setCostPerUnit] = useState('');
  const [sortOrder, setSortOrder] = useState<number | ''>('');
  const [isActive, setIsActive] = useState(true);

  // Populate state when editing
  useEffect(() => {
    if (isEditMode && fuelTypeData) {
      setFuelName(fuelTypeData.name || '');
      setSelectedUnit(fuelTypeData.unit || '');
      setCostPerUnit(fuelTypeData.costPerUnit?.toString() || '');
      setSortOrder(fuelTypeData.sortOrder ?? '');
      setIsActive(fuelTypeData.isActive ?? true);
    } else {
      // Reset for new
      setFuelName('');
      setSelectedUnit('');
      setCostPerUnit('');
      setSortOrder('');
      setIsActive(true);
    }
  }, [fuelTypeData, isEditMode]);

  const handleSubmit = () => {
    const payload: FuelType = {
      id: fuelTypeData?.id || 0,
      name: fuelName,
      unit: selectedUnit,
      costPerUnit: parseFloat(costPerUnit),
      sortOrder: Number(sortOrder),
      isActive
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={openFT} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            <Category /> {isEditMode ? 'Edit Fuel Type' : 'Add New Fuel Type'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Paper elevation={0} sx={{ p: 2, backgroundColor: 'transparent', borderRadius: 2 }}>
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fuel Name"
                variant="outlined"
                sx={commonTextFieldStyles}
                required
                value={fuelName}
                onChange={(e) => setFuelName(e.target.value)}
              />
              <TextField
                select
                fullWidth
                label="Measurement Unit"
                variant="outlined"
                sx={commonTextFieldStyles}
                value={selectedUnit}
                onChange={(e) => setSelectedUnit(e.target.value)}
              >
                {units.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cost/Unit"
                variant="outlined"
                sx={commonTextFieldStyles}
                value={costPerUnit}
                onChange={(e) => setCostPerUnit(e.target.value)}
              />
              <TextField
                fullWidth
                label="Sort Order"
                type="number"
                variant="outlined"
                sx={commonTextFieldStyles}
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
              />
            </Grid>

            {/* Checkbox */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active Fuel Type"
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' } }}
          onClick={handleSubmit}
        >
          {isEditMode ? 'Update Fuel Type' : 'Create Fuel Type'}
        </Button>
        <Button
          variant="contained"
          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' } }}
          onClick={onClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewFuelTypePopUp;